/**
 * Angebots-Detail.
 *
 * Zweispaltig: links Status, Beteiligte, Mail-Verlauf, Gesprächsnotizen und
 * Statusverlauf – rechts der Datensatz und die weiteren Angebote des Kunden.
 *
 * Zwei Warnbanner sind bewusst dauerhaft, nicht flüchtig:
 *  - fehlgeschlagener Sync zu Monday/SharePoint
 *  - in Monday pausiert, aber ohne Wiedervorlage-Datum
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { IconMonday, IconOrdner } from '../komponenten/Icons';
import { AuftragModal } from '../modals/AuftragModal';
import { VersendetModal } from '../modals/VersendetModal';
import { GewerkModal } from '../modals/GewerkModal';
import { AnrufModal } from '../modals/AnrufModal';
import { MailVorschauModal } from '../modals/MailVorschauModal';
import { betragFormatieren, betragLesen, dat, eur, gruppiere } from '../hilfen/format';
import type { Angebot, AngebotAntwort } from '../typen';
import './detail.css';


export function AngebotDetail() {
  const { id } = useParams();
  const angebotId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toastZeigen, nutzer } = useKora();
  const meta = useMeta();

  /** Aufgeklappte Löschabfrage. Zweistufig, weil der Schritt endgültig ist. */
  const [loeschAbfrage, setLoeschAbfrage] = useState(false);
  const [auftragOffen, setAuftragOffen] = useState(false);
  const [versendetOffen, setVersendetOffen] = useState(false);
  const [gewerkOffen, setGewerkOffen] = useState(false);
  const [anrufOffen, setAnrufOffen] = useState(false);
  const [mailOffen, setMailOffen] = useState(false);
  const [kalkWechsel, setKalkWechsel] = useState(false);
  const [auftragFeld, setAuftragFeld] = useState<string | null>(null);

  const abfrage = useQuery({
    queryKey: ['angebot', angebotId],
    queryFn: () => api.hole<Angebot>(`/angebote/${angebotId}`),
    enabled: Number.isFinite(angebotId),
  });

  const alleAbfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
  });

  function aktualisieren() {
    void queryClient.invalidateQueries({ queryKey: ['angebot', angebotId] });
    void queryClient.invalidateQueries({ queryKey: ['angebote'] });
  }

  /**
   * Zuständigkeit wechseln. Schreibt auch die Personenspalte in Monday –
   * dort schaut man ja gerade deshalb hin.
   */
  const kalkulantMutation = useMutation({
    mutationFn: (kalkulant: string) =>
      api.aendere<Angebot>(`/angebote/${angebotId}/kalkulant`, { kalkulant }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      setKalkWechsel(false);
      toastZeigen('Zuständigkeit gewechselt – Monday ist nachgezogen.');
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Wechsel fehlgeschlagen.'),
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      api.sende<AngebotAntwort>(`/angebote/${angebotId}/status`, { status }),
    onSuccess: (a) => {
      aktualisieren();
      if (a.sync.fehler) toastZeigen('Status gespeichert – der Abgleich meldet ein Problem.');
    },
  });

  /**
   * Löscht den Vorgang in Kora.
   *
   * Nur für Fehlgriffe und Testanlagen, deshalb der Administration
   * vorbehalten. Kora räumt ausschließlich die eigenen Daten weg; was in
   * Monday und SharePoint liegt, bleibt stehen und wird in der Rueckmeldung
   * benannt. Anschließend zurück zur Liste – die Seite hätte keinen
   * Datensatz mehr zum Anzeigen.
   */
  const loeschMutation = useMutation({
    mutationFn: () =>
      api.loesche<{ nr: number; nummerFrei: boolean; offen: string[] }>(
        `/angebote/${angebotId}`,
      ),
    onSuccess: (a) => {
      const teile = [`Angebot ${a.nr} gelöscht`];
      if (a.nummerFrei) teile.push('die Nummer ist wieder frei');
      if (a.offen.length) teile.push(`nicht entfernt: ${a.offen.join(', ')}`);
      toastZeigen(teile.join(' - '));
      void queryClient.invalidateQueries();
      navigate('/cockpit');
    },
    onError: (f: Error) => toastZeigen(f.message),
  });

  const syncMutation = useMutation({
    mutationFn: () => api.sende<AngebotAntwort>(`/angebote/${angebotId}/sync`),
    onSuccess: (a) => {
      aktualisieren();
      if (a.toast) toastZeigen(a.toast);
    },
  });

  const wertMutation = useMutation({
    mutationFn: (auftrag: number | null) =>
      api.aendere<Angebot>(`/angebote/${angebotId}/auftragswert`, { auftrag }),
    onSuccess: aktualisieren,
  });



  const a = abfrage.data;

  const geschwister = useMemo(
    () => (alleAbfrage.data ?? []).filter((x) => a && x.kunde.id === a.kunde.id && x.id !== a.id),
    [alleAbfrage.data, a],
  );

  const verlauf = useMemo(() => {
    if (!a) return [];
    const absteigend = [...a.verlauf].reverse();
    return absteigend.map((h, i) => {
      const bis = i === 0 ? meta.heute : absteigend[i - 1]!.datum;
      const dauer = Math.round(
        (new Date(bis + 'T00:00:00').getTime() - new Date(h.datum + 'T00:00:00').getTime()) /
          86_400_000,
      );
      return {
        ...h,
        dauerText:
          dauer <= 0
            ? 'heute'
            : `${dauer} ${dauer === 1 ? 'Tag' : 'Tage'} ${i === 0 ? 'offen' : 'gedauert'}`,
        linie: i < absteigend.length - 1,
        aktuell: i === 0,
      };
    });
  }, [a, meta.heute]);

  if (abfrage.isLoading) {
    return <div className="eh-inhalt">Angebot wird geladen …</div>;
  }
  if (!a) {
    return (
      <div className="eh-inhalt">
        <div className="eh-karte">Dieses Angebot gibt es nicht (mehr).</div>
      </div>
    );
  }

  const erinnBadge = !meta.aktiveStatus.includes(a.status)
    ? `Kein Nachfassen – Status ${a.status}`
    : a.nachfass.antwortAm
      ? `Kundenantwort am ${dat(a.nachfass.antwortAm)} erkannt – kein Anruf nötig`
      : !a.nachfass.aktiv
        ? 'Nachfassen pausiert'
        : a.nachfass.mailVerfallen
          ? `Erinnerung entfällt – seit ${dat(a.nachfass.anrufAb)} ist der Anruf dran`
          : a.nachfass.erledigt < 1
            ? `1. Erinnerung geht automatisch am ${dat(a.nachfass.mailAm)} raus`
            : a.nachfass.anrufFaellig
              ? `Anruf ausstehend seit ${dat(a.nachfass.anrufAb)}`
              : `Anruf fällig am ${dat(a.nachfass.anrufAb)}`;

  const erinnTon = a.nachfass.antwortAm
    ? 'gruen'
    : a.nachfass.anrufFaellig
      ? 'rot'
      : 'neutral';

  /**
   * Versandstand.
   *
   * Maßgeblich ist die Historie, nicht das Statusfeld: Ein Vorgang kann
   * mehrfach hinausgegangen sein, und die Uhr zählt ab dem letzten Mal.
   */
  const versendet = a.versandHistorie.length > 0;
  const letzterVersand = a.versandHistorie.at(-1) ?? null;

  /**
   * Der Fortschritt als Zeitleiste – nur Anzeige, nichts zum Klicken.
   *
   * Gesetzt wird oben über „Status ändern". Hier steht, wo der Vorgang steht
   * und wie er dahin kam: je Station, wer sie gesetzt hat und wann. Der frühere
   * Erklärtext ist entfallen – er sagte, warum etwas noch nicht geht, und das
   * beantwortet sich, sobald die Stationen leer oder gefüllt sind.
   *
   * „Rückmeldung erfassen" steht bewusst nicht hier: Ein Kundenanruf ist kein
   * Status, den jemand gesetzt hat, sondern ein Ereignis.
   */
  const stationen = ['START', 'in Arbeit', 'Freigabe Technik', 'Gesendet', 'Auftrag erteilt', 'Abgenommen'];

  const erreicht = new Map<string, { user: string; datumText: string }>();
  for (const v of a.verlauf) {
    // Bei mehrfach durchlaufenen Stationen zählt der letzte Durchgang.
    erreicht.set(v.status, { user: v.user, datumText: v.datumText });
  }
  /*
   * START ist bei jedem Vorgang durchlaufen – ohne Anlage gäbe es ihn nicht.
   * Für in Kora angelegte Angebote steht der Eintrag im Verlauf; für die aus
   * Monday übernommenen fehlt er, dort bleibt wenigstens das Anlagedatum.
   */
  if (!erreicht.has('START')) {
    erreicht.set('START', { user: '', datumText: dat(a.angelegtAm) });
  }

  /**
   * Wohin führt ein gewählter Status?
   *
   * Manche Wechsel brauchen Angaben – ein Versanddatum, eine Auftragshöhe,
   * eine Begründung. Statt sie stumm zu setzen, öffnet die Auswahl den
   * passenden Dialog. So gibt es einen Ort für den Statuswechsel, ohne dass
   * Pflichtangaben verlorengehen.
   */
  function statusWaehlen(ziel: string) {
    if (!ziel) return;
    if (ziel === 'Gesendet') { setVersendetOffen(true); return; }
    if (ziel === 'Auftrag erteilt') { setAuftragOffen(true); return; }
    if (ziel === 'Wiedervorlage' || ziel === 'Abgesagt') { setAnrufOffen(true); return; }
    statusMutation.mutate(ziel);
  }

  const erinnLog = a.erinnerungen
    .map(
      (e) =>
        `1. Erinnerung ${e.art === 'auto' ? 'automatisch versendet' : e.artText} am ${e.datumText} im Namen von ${e.user}` +
        (e.trockenlauf ? ' (Testbetrieb: nur als Entwurf abgelegt)' : ''),
    )
    .concat(
      a.nachfass.antwortAm
        ? [`Kundenantwort am ${dat(a.nachfass.antwortAm)} im Outlook-Postfach erkannt`]
        : [],
    )
    .join('  ·  ');

  const wertOffen = a.auftrag === null || a.auftrag === 0;
  const wertWarnung = a.status === 'Auftrag erteilt' && wertOffen;

  const auftragHinweis = wertWarnung
    ? 'Status ist Auftrag erteilt – bitte Auftragshöhe eintragen, der Wert fließt in die Gewinnprognose ein.'
    : a.status === 'Auftrag erteilt'
      ? 'Auftrag erteilt – der Wert zählt in die Gewinnprognose, bis der Vorgang abgenommen ist.'
      : versendet
        ? 'Kann vorgemerkt werden. Bei „Auftrag erteilt“ wird der Wert noch einmal vorgelegt und muss bestätigt werden.'
        : 'Kann vorgemerkt werden. Verbindlich wird der Wert erst mit „Auftrag erteilt“.';

  const datensatz = [
    { label: 'Laufende Nummer', wert: String(a.nr) },
    { label: 'Kategorie', wert: `${a.prefix} – ${a.kategorie}` },
    { label: 'Kalkulant', wert: a.kalkulant },
    { label: 'Kunde', wert: a.kunde.name },
    { label: 'Ort', wert: a.kunde.ort },
    { label: 'E-Mail', wert: a.kunde.mail || '—' },
    { label: 'Statuswechsel', wert: String(a.verlauf.length) },
    { label: 'Auftragshöhe', wert: eur(a.auftrag) },
  ];

  return (
    <div className="detail">
      <button type="button" className="detail-zurueck" onClick={() => navigate(-1)}>
        ← Zurück
      </button>

      <div className="detail-kopf">
        <div>
          <div className="detail-kategorie">
            <span className="detail-prefix">{a.prefix}</span>
            <span className="detail-kategorie-text">
              {a.kategorie} · angelegt {dat(a.angelegtAm)}
            </span>
          </div>
          <div className="detail-name">{a.name}</div>
          {/*
            Zwei Links, keine Karten.
            Als Schaltflächen gestaltet sagten sie dasselbe wie die Statuswechsel
            darunter – dabei tun sie etwas völlig anderes: Sie führen woandershin,
            sie verändern nichts. Knöpfe bleiben den Statuswechseln vorbehalten,
            sonst sagt die Form nichts mehr aus.
  
            Der Ordner öffnet sich im Explorer. Das geht aus dem Browser nur über
            ein eigenes Protokoll – ein file://-Link wird blockiert. Eingerichtet
            wird es mit werkzeuge/explorer/einrichten.cmd; fehlt es, passiert beim
            Klick nichts.
          */}
          <div className="detail-verknuepfungen">
            {a.mondayUrl && (
              <a className="detail-link" href={a.mondayUrl} target="_blank" rel="noreferrer">
                <IconMonday groesse={14} />
                Projekt öffnen
              </a>
            )}
            <a
              className="detail-link"
              href={`kora://oeffne?art=${a.status === 'Auftrag erteilt' || a.status === 'Abgenommen' ? 'auftrag' : 'angebot'}&ordner=${encodeURIComponent(a.ordnerName)}`}
              title={`Öffnet ${a.ordnerName} im Windows-Explorer`}
            >
              <IconOrdner groesse={14} />
              Ordner öffnen
            </a>
          </div>
        </div>

      </div>

      <div className="detail-raster">
        <div className="detail-spalte">
          {/* ---- Statuskarte ---- */}
          <div className="detail-karte">
            <div className="detail-status-kopf">
              <div>
                <div className="detail-label">Aktueller Status</div>
                <div className="detail-status-zeile">
                  <span className="detail-status-punkt" style={{ background: a.statusPunkt }} />
                  <span
                    className="detail-status-text"
                    style={{
                      color: a.status === 'Auftrag erteilt' ? 'var(--eh-gruen)' : 'var(--eh-text)',
                    }}
                  >
                    {a.status}
                  </span>
                  <span
                    className="detail-alter"
                    style={{ background: a.ampel.bg, color: a.ampel.fg }}
                  >
                    {a.alterText} im Status
                  </span>
                </div>

                {(a.pausiert || a.wvFaellig) && (
                  <div className="detail-pause">
                    <span className="detail-pause-punkt" />
                    <span>
                      {a.pausiert
                        ? `Wiedervorlage bis ${dat(a.wiedervorlage)}`
                        : `Wiedervorlage fällig seit ${dat(a.wiedervorlage)}`}
                    </span>
                  </div>
                )}

                {a.status === 'Auftrag erteilt' && a.auftrag ? (
                  <div className="detail-auftragswert">
                    <span className="detail-auftragswert-label">Auftragswert</span>
                    <span className="detail-auftragswert-zahl">{a.auftragText}</span>
                    <span className="detail-auftragswert-note">netto, vor Steuern</span>
                  </div>
                ) : null}
              </div>

              <div className="detail-aktionen">
                {/*
                  Ein Ort für den Statuswechsel – als Knopf erkennbar, weil er
                  etwas verändert. Braucht ein Status Angaben, öffnet die
                  Auswahl den passenden Dialog statt ihn stumm zu setzen.
                */}
                <div className="detail-kopfknoepfe">
                  <label className="detail-statuswahl">
                    <span>Status ändern</span>
                    <select
                      value=""
                      disabled={statusMutation.isPending}
                      onChange={(e) => {
                        statusWaehlen(e.target.value);
                        e.target.value = '';
                      }}
                    >
                      <option value="">wählen …</option>
                      {meta.status
                        .filter((x) => x !== a.status)
                        .map((x) => (
                          <option key={x} value={x}>
                            {x}
                          </option>
                        ))}
                    </select>
                  </label>

                  <button
                    type="button"
                    className="detail-kopfknopf"
                    onClick={() => setAnrufOffen(true)}
                  >
                    Rückmeldung erfassen
                  </button>

                  {a.ergaenzbareGewerke.length > 0 && (
                    <button
                      type="button"
                      className="detail-kopfknopf"
                      onClick={() => setGewerkOffen(true)}
                      title="Beschattung oder Insektenschutz unter derselben Nummer ergänzen"
                    >
                      Gewerk ergänzen
                    </button>
                  )}
                </div>


              </div>
            </div>

            {a.wvKonflikt && (
              <div className="detail-warnung">
                <div className="detail-warnung-text">
                  <span className="detail-warnung-icon">!</span>
                  <div>
                    <div className="detail-warnung-titel">
                      In Monday pausiert, aber kein Wiedervorlage-Datum hinterlegt – bitte nachtragen
                    </div>
                    <div className="detail-warnung-unter">
                      Ohne Datum taucht das Angebot nie wieder von selbst auf.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="detail-warnung-knopf"
                  onClick={() => setAnrufOffen(true)}
                >
                  Anruf nachtragen
                </button>
              </div>
            )}

            {a.syncFehler && (
              <div className="detail-warnung">
                <div className="detail-warnung-text">
                  <span className="detail-warnung-icon">!</span>
                  <div>
                    <div className="detail-warnung-titel">
                      Status lokal gesetzt, Sync zu Monday/SharePoint fehlgeschlagen
                    </div>
                    <div className="detail-warnung-unter">{a.syncFehler}</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="detail-warnung-knopf"
                  disabled={syncMutation.isPending}
                  onClick={() => syncMutation.mutate()}
                >
                  {syncMutation.isPending ? 'Läuft …' : 'Erneut versuchen'}
                </button>
              </div>
            )}

            {/* ---- Beteiligte ---- */}
            <div className="detail-beteiligte">
              <div className="detail-personen">
                <div className="detail-person">
                  <span className="eh-avatar detail-avatar">{a.kalkInitialen}</span>
                  <span className="detail-person-text">
                    <span className="detail-person-name">{a.kalkulant}</span>
                    {kalkWechsel ? (
                      <select
                        className="detail-kalk-auswahl"
                        value={a.kalkulant}
                        disabled={kalkulantMutation.isPending}
                        onChange={(e) => kalkulantMutation.mutate(e.target.value)}
                      >
                        {meta.team
                          .filter((t) => t.imVertrieb)
                          .map((t) => (
                            <option key={t.name} value={t.name}>
                              {t.name}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <span className="detail-person-rolle">
                        Kalkulant · fachlich verantwortlich
                        <button
                          type="button"
                          className="detail-kalk-knopf"
                          title="Zuständigkeit wechseln"
                          onClick={() => setKalkWechsel(true)}
                        >
                          wechseln
                        </button>
                      </span>
                    )}
                  </span>
                </div>

                {/*
                  Der Versender erscheint erst, wenn tatsächlich versendet wurde.
                  „Nicht ermittelt" bei einem Vorgang, der nie hinausging, ist
                  keine Auskunft, sondern Rauschen.
                */}
                {versendet && (
                  <div className="detail-person">
                    <span
                      className="eh-avatar detail-avatar"
                      style={
                        a.versAbweichend
                          ? { background: 'var(--eh-rot-flaeche)', color: 'var(--eh-rot)' }
                          : undefined
                      }
                    >
                      {a.versInitialen}
                    </span>
                    <span className="detail-person-text">
                      <span className="detail-person-name">{letzterVersand?.versender}</span>
                      <span className="detail-person-rolle">
                        Versender · Erinnerungen gehen in seinem Namen raus
                      </span>
                    </span>
                  </div>
                )}
              </div>

              <div>
                <span className={`detail-erinn-badge ${erinnTon}`}>{erinnBadge}</span>
              </div>
            </div>

            {erinnLog && <div className="detail-vers-text">{erinnLog}</div>}

            {/* ---- Versandhistorie ---- */}
            {versendet && (
              <div className="detail-abschnitt">
                <div className="detail-abschnitt-titel">
                  {a.versandHistorie.length === 1 ? 'Versendet' : 'Versandhistorie'}
                </div>
                <div className="detail-versandliste">
                  {a.versandHistorie.map((v) => (
                    <div
                      key={v.nummer}
                      className={`detail-versand ${v.nummer === a.versandHistorie.length ? 'aktuell' : ''}`}
                    >
                      <span className="detail-versand-nr">{v.nummer}.</span>
                      <span className="detail-versand-datum">{v.datumText}</span>
                      <span className="detail-versand-wer">
                        durch {v.versender}
                        {v.erfasstDurch && (
                          <span className="detail-versand-erfasst">
                            {' '}
                            · eingetragen von {v.erfasstDurch}
                          </span>
                        )}
                      </span>
                      {v.nummer === a.versandHistorie.length && a.versandHistorie.length > 1 && (
                        <span className="detail-versand-marke">Nachfass-Uhr läuft ab hier</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ---- Fortschritt ---- */}
            <div className="detail-abschnitt">
              <div className="detail-abschnitt-titel">Fortschritt</div>
              <div className="detail-band">
                {stationen.map((st) => {
                  const wann = erreicht.get(st);
                  const jetzt = st === a.status;
                  return (
                    <div
                      key={st}
                      className={`detail-station ${jetzt ? 'jetzt' : wann ? 'erreicht' : 'offen'}`}
                    >
                      <span className="detail-station-name">{st}</span>
                      <span className="detail-station-wer">
                        {wann
                          ? [wann.datumText, wann.user && `durch ${wann.user}`]
                              .filter(Boolean)
                              .join(' · ')
                          : '—'}
                      </span>
                      {/*
                        Nur an der laufenden Station: wie lange der Vorgang hier
                        schon steht. An erledigten Stationen wäre die Zahl
                        bedeutungslos, an offenen gibt es sie nicht.
                      */}
                      {jetzt && (
                        <span className="detail-station-dauer">{a.alterText} im Status</span>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>

            {/* ---- Auftragshöhe ---- */}
            <div className="detail-abschnitt">
              <div className={`detail-auftragsfeld ${wertWarnung ? 'warnung' : ''}`}>
                <div className="detail-auftragsfeld-zeile">
                  <label className="detail-auftrag-label">
                    <span>Auftragshöhe · netto, vor Steuern</span>
                    <span className="detail-auftrag-eingabe">
                      <input
                        value={
                          auftragFeld ?? (a.auftrag ? gruppiere(a.auftrag) : '')
                        }
                        placeholder="0"
                        onChange={(e) => setAuftragFeld(betragFormatieren(e.target.value))}
                        onBlur={() => {
                          if (auftragFeld === null) return;
                          wertMutation.mutate(betragLesen(auftragFeld));
                          setAuftragFeld(null);
                        }}
                      />
                      <span>€</span>
                    </span>
                  </label>

                </div>
                <div
                  className="detail-auftrag-hinweis"
                  style={{ color: wertWarnung ? 'var(--eh-rot)' : 'var(--eh-text-5)' }}
                >
                  {auftragHinweis}
                </div>
              </div>
            </div>
          </div>

          {/* ---- Mail-Verlauf ---- */}
          <div className="detail-karte">
            <div className="detail-karte-kopf">
              <div className="detail-label">Mail-Verlauf</div>
              <div className="detail-anzahl">
                {a.erinnerungen.length === 1 ? '1 Mail' : `${a.erinnerungen.length} Mails`}
              </div>
            </div>
            <div className="detail-mailliste">
              {a.erinnerungen.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  className="detail-mailzeile"
                  onClick={() => setMailOffen(true)}
                >
                  <span className="detail-mail-art">{e.stufe}. Erinnerung</span>
                  <span className="detail-mail-zeit">
                    {e.wochentag}, {e.datumText} · {e.zeit || '—'}
                  </span>
                  <span className="detail-mail-user">{e.user}</span>
                  <span className="detail-mail-status">
                    <span className={`detail-mail-badge ${e.art === 'auto' ? 'gruen' : ''}`}>
                      {e.trockenlauf ? 'Entwurf im Testbetrieb' : e.artText}
                    </span>
                    <span className="detail-mail-chevron">›</span>
                  </span>
                </button>
              ))}
              {a.erinnerungen.length === 0 && (
                <div className="detail-leer">
                  {a.nachfass.mailVerfallen
                    ? 'Keine Erinnerungsmail versendet – dafür ist es inzwischen zu spät. ' +
                      'Kora holt sie nicht nach, weil eine automatische Nachfrage so lange nach ' +
                      'dem Angebot gedankenlos wirkt. Stattdessen steht der Anruf an.'
                    : 'Noch keine Erinnerungsmail zu diesem Angebot versendet.'}
                </div>
              )}
            </div>
          </div>

          {/* ---- Gesprächsnotizen ---- */}
          <div className="detail-karte">
            <div className="detail-karte-kopf">
              <div className="detail-label">Rückmeldungen</div>
              <div className="detail-anzahl">
                {a.anrufe.length === 1 ? '1 Eintrag' : `${a.anrufe.length} Einträge`}
              </div>
            </div>
            <div className="detail-anrufliste">
              {a.anrufe.map((c) => (
                <div
                  key={c.id}
                  className="detail-anruf"
                  style={{ borderLeftColor: c.farbe.akzent }}
                >
                  <div className="detail-anruf-kopf">
                    <span className="detail-anruf-zeit">
                      {c.wochentag}, {c.datumText} · {c.zeit} · {c.kanalLabel}
                    </span>
                    <span className="detail-anruf-user">{c.user}</span>
                  </div>
                  <div className="detail-anruf-notiz">{c.notiz}</div>
                  <div className="detail-anruf-fuss">
                    <span
                      className="detail-anruf-badge"
                      style={{ background: c.farbe.bg, color: c.farbe.fg }}
                    >
                      {c.ergebnisLabel}
                    </span>
                    {/*
                      Eine Rückmeldung ist kein Status – aber sie zieht oft
                      einen nach sich. Der steht hier, damit man den Zusammenhang
                      sieht, ohne den Statusverlauf danebenzulegen.
                    */}
                    {c.statusGesetzt && (
                      <span className="detail-anruf-folge">→ {c.statusGesetzt}</span>
                    )}
                    <span className="detail-anruf-zusatz">
                      {c.wiedervorlage ? `Wiedervorlage am ${dat(c.wiedervorlage)}` : c.sonst}
                    </span>
                  </div>
                </div>
              ))}
              {a.anrufe.length === 0 && (
                <div className="detail-leer">Noch keine Rückmeldung erfasst.</div>
              )}
            </div>
          </div>

          {/* ---- Statusverlauf ---- */}
          <div className="detail-karte">
            <div className="detail-label" style={{ marginBottom: 18 }}>
              Statusverlauf
            </div>
            <div>
              {verlauf.map((h, i) => (
                <div className="detail-verlauf-zeile" key={`${h.datum}-${i}`}>
                  <div className="detail-verlauf-spur">
                    <span className="detail-verlauf-punkt" style={{ background: h.punkt }} />
                    <span
                      className="detail-verlauf-linie"
                      style={{ background: h.linie ? 'var(--eh-rahmen)' : 'transparent' }}
                    />
                  </div>
                  <div className="detail-verlauf-inhalt">
                    <div className="detail-verlauf-kopf">
                      <span className="detail-verlauf-status">{h.status}</span>
                      <span className="detail-verlauf-datum">{h.datumText}</span>
                      <span className="detail-verlauf-dauer">{h.dauerText}</span>
                    </div>
                    <div className="detail-verlauf-notiz">{h.notiz || '—'}</div>
                    <div className="detail-verlauf-user">
                      {h.user}
                      {h.aktuell ? ' · aktuell' : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/*
            Löschen steht bewusst ganz unten und ohne Farbe: Es ist kein
            Arbeitsschritt, sondern die Notausfahrt für Fehlgriffe und
            Testanlagen. Nur für die Administration sichtbar, und erst nach
            einer Rückfrage, die benennt, was verschwindet und was bleibt.
          */}
          {nutzer?.istAdmin && (
            <div className="detail-notausfahrt">
              {!loeschAbfrage ? (
                <button type="button" onClick={() => setLoeschAbfrage(true)}>
                  Angebot löschen
                </button>
              ) : (
                <div className="detail-loeschfeld">
                  <div className="detail-loeschtext">
                    Löscht {a.name} samt Verlauf, Rückmeldungen und Erinnerungen aus Kora.
                    {a.mondayUrl || a.ordnerName ? (
                      <>
                        {' '}
                        Das Monday-Element und der SharePoint-Ordner bleiben bestehen und
                        müssen dort von Hand entfernt werden.
                      </>
                    ) : null}{' '}
                    Der Schritt lässt sich nicht rückgängig machen.
                  </div>
                  <div className="detail-loeschknoepfe">
                    <button
                      type="button"
                      className="detail-loeschen-ja"
                      disabled={loeschMutation.isPending}
                      onClick={() => loeschMutation.mutate()}
                    >
                      {loeschMutation.isPending ? 'Löscht …' : 'Endgültig löschen'}
                    </button>
                    <button type="button" onClick={() => setLoeschAbfrage(false)}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ---- Rechte Spalte ---- */}
        <div className="detail-seitenspalte">
          <div className="detail-karte detail-datensatz">
            <div className="detail-label">Datensatz</div>
            {datensatz.map((m) => (
              <div className="detail-datenzeile" key={m.label}>
                <span className="detail-datenlabel">{m.label}</span>
                <span className="detail-datenwert">{m.wert}</span>
              </div>
            ))}
          </div>

          <div className="detail-karte detail-geschwister">
            <div className="detail-label" style={{ marginBottom: 13 }}>
              Weitere Angebote des Kunden
            </div>
            <div className="detail-geschwister-liste">
              {geschwister.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className="detail-geschwister-knopf"
                  onClick={() => navigate(`/angebot/${g.id}`)}
                >
                  <span className="detail-geschwister-name">{g.name}</span>
                  <span className="detail-geschwister-meta">
                    {g.status} · {g.alterText}
                  </span>
                </button>
              ))}
              {geschwister.length === 0 && (
                <div className="detail-leer">Einzelangebot — keine weiteren Vorgänge.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {gewerkOffen && (
        <GewerkModal
          angebot={a}
          onSchliessen={() => setGewerkOffen(false)}
          onFertig={(neueId) => {
            aktualisieren();
            navigate(`/angebot/${neueId}`);
          }}
        />
      )}
      {versendetOffen && (
        <VersendetModal
          angebot={a}
          onSchliessen={() => setVersendetOffen(false)}
          onFertig={aktualisieren}
        />
      )}
      {auftragOffen && (
        <AuftragModal
          angebot={a}
          onSchliessen={() => setAuftragOffen(false)}
          onFertig={aktualisieren}
        />
      )}
      {anrufOffen && (
        <AnrufModal angebot={a} onSchliessen={() => setAnrufOffen(false)} onFertig={aktualisieren} />
      )}
      {mailOffen && <MailVorschauModal angebot={a} onSchliessen={() => setMailOffen(false)} />}
    </div>
  );
}

